import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'antd';
import Button from 'components/controls/Button';
import BoxInput from 'components/controls/BoxInput';
import _authService from 'modules/AuthService';
import { otpResendTimeout } from 'constants/constants';

// Sprint 2.6.1 (Enterprise Identity Modernization): reusable Mobile OTP + Email OTP verification panel —
// the active replacement for Aadhaar/SurePass verification on the candidate registration form. Built
// config-flag-driven (mobileEnabled/emailEnabled) and copy-agnostic (labels passed as props, no hardcoded
// institution wording) so it is reusable across tenants. Mirrors ForgotPassword_new.jsx's proven
// send/verify/resend-timer mechanics, using BoxInput for 6-digit entry.
// Sprint 2.6.2 (Candidate Experience Modernization): swapped antd's default-styled <Button> for the
// form's own shared control (components/controls/Button, ".button-global" classes) so Send/Verify/Resend
// visually match every other button in the registration form instead of standing out as antd chrome.
const RESEND_SECONDS = (Number(otpResendTimeout) || 3) * 60;

const initialChannelState = {
  sent: false,
  verified: false,
  otp: '',
  timeLeft: 0,
  isResendDisabled: false,
  error: '',
  loading: false
};

const IdentityVerificationPanel = ({
  postId,
  mobileNumber,
  email,
  mobileEnabled,
  emailEnabled,
  mobileLabel,
  emailLabel,
  onVerified
}) => {
  const [sessionGuid, setSessionGuid] = useState(null);
  const [mobile, setMobile] = useState(initialChannelState);
  const [emailState, setEmailState] = useState(initialChannelState);
  const mobileTimerRef = useRef(null);
  const emailTimerRef = useRef(null);

  useEffect(() => {
    // Intentionally reads .current at cleanup time, not a value captured at mount — these refs hold
    // interval ids that are only assigned later (on first Send OTP), not DOM nodes.
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      clearInterval(mobileTimerRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      clearInterval(emailTimerRef.current);
    };
  }, []);

  const startTimer = (timerRef, setState) => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setState((prev) => {
        if (prev.timeLeft > 1) {
          return { ...prev, timeLeft: prev.timeLeft - 1 };
        }
        clearInterval(timerRef.current);
        return { ...prev, timeLeft: 0, isResendDisabled: false };
      });
    }, 1000);
  };

  const notifyIfComplete = useCallback((token) => {
    if (token) {
      onVerified(token);
    }
  }, [onVerified]);

  const sendMobileOtp = async () => {
    setMobile((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await _authService.GenerateMobileOTP({
        SessionGuid: sessionGuid,
        Applied_PostId: postId,
        Mobile_Number: mobileNumber,
        Email: email
      });
      const data = response && response.data;
      if (data && data.session_guid) {
        setSessionGuid(data.session_guid);
      }
      if (data && data.success) {
        setMobile((prev) => ({
          ...prev, loading: false, sent: true, timeLeft: RESEND_SECONDS, isResendDisabled: true, error: ''
        }));
        startTimer(mobileTimerRef, setMobile);
      } else {
        setMobile((prev) => ({ ...prev, loading: false, error: (data && data.message) || 'Unable to send OTP. Please try again.' }));
      }
    } catch (e) {
      setMobile((prev) => ({ ...prev, loading: false, error: 'Unable to send OTP. Please try again.' }));
    }
  };

  const verifyMobileOtp = async () => {
    setMobile((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await _authService.VerifyMobileOTP({ SessionGuid: sessionGuid, Otp: mobile.otp });
      const data = response && response.data;
      if (data && data.success) {
        setMobile((prev) => ({ ...prev, loading: false, verified: true, error: '' }));
        notifyIfComplete(data.verification_token);
      } else {
        setMobile((prev) => ({ ...prev, loading: false, error: (data && data.message) || 'Invalid OTP.' }));
      }
    } catch (e) {
      setMobile((prev) => ({ ...prev, loading: false, error: 'Unable to verify OTP. Please try again.' }));
    }
  };

  const sendEmailOtp = async () => {
    setEmailState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await _authService.GenerateEmailOTP({
        SessionGuid: sessionGuid,
        Applied_PostId: postId,
        Mobile_Number: mobileNumber,
        Email: email
      });
      const data = response && response.data;
      if (data && data.session_guid) {
        setSessionGuid(data.session_guid);
      }
      if (data && data.success) {
        setEmailState((prev) => ({
          ...prev, loading: false, sent: true, timeLeft: RESEND_SECONDS, isResendDisabled: true, error: ''
        }));
        startTimer(emailTimerRef, setEmailState);
      } else {
        setEmailState((prev) => ({ ...prev, loading: false, error: (data && data.message) || 'Unable to send OTP. Please try again.' }));
      }
    } catch (e) {
      setEmailState((prev) => ({ ...prev, loading: false, error: 'Unable to send OTP. Please try again.' }));
    }
  };

  const verifyEmailOtp = async () => {
    setEmailState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await _authService.VerifyEmailOTP({ SessionGuid: sessionGuid, Otp: emailState.otp });
      const data = response && response.data;
      if (data && data.success) {
        setEmailState((prev) => ({ ...prev, loading: false, verified: true, error: '' }));
        notifyIfComplete(data.verification_token);
      } else {
        setEmailState((prev) => ({ ...prev, loading: false, error: (data && data.message) || 'Invalid OTP.' }));
      }
    } catch (e) {
      setEmailState((prev) => ({ ...prev, loading: false, error: 'Unable to verify OTP. Please try again.' }));
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const renderChannel = (channelKey, state, label, valuePlaceholder, onSend, onVerify, onOtpChange) => (
    <div className="ent-otp-channel">
      <div className="ent-otp-channel-header">
        <span>{label} <strong>({valuePlaceholder})</strong></span>
        {state.verified && <span className="ent-otp-channel-verified"><Icon type="check-circle" /> Verified</span>}
      </div>
      {!state.verified && (
        <>
          {!state.sent && (
            <Button
              type="button"
              className="button-global button-primary autoHeight mw125"
              disabled={state.loading}
              onClick={onSend}
              value={state.loading ? 'Sending…' : 'Send OTP'}
            />
          )}
          {state.sent && (
            <>
              <div className="ent-otp-boxes">
                <BoxInput
                  boxInputNumberCount={6}
                  boxInputValue=""
                  onChange={onOtpChange}
                />
                <Button
                  type="button"
                  className="button-global button-primary autoHeight mw125"
                  disabled={state.loading || !state.otp || state.otp.length !== 6}
                  onClick={onVerify}
                  value={state.loading ? 'Verifying…' : 'Verify'}
                />
              </div>
              <div className="ent-otp-timer">
                {state.isResendDisabled
                  ? `Resend OTP in ${formatTime(state.timeLeft)}`
                  : <button type="button" className="ent-otp-resend-link" onClick={onSend}>Resend OTP</button>}
              </div>
            </>
          )}
          {state.error && <div className="ent-otp-error">{state.error}</div>}
        </>
      )}
    </div>
  );

  if (!mobileEnabled && !emailEnabled) {
    return null;
  }

  return (
    <div className="ent-otp-panel">
      {mobileEnabled && renderChannel(
        'mobile', mobile, mobileLabel, mobileNumber,
        sendMobileOtp, verifyMobileOtp,
        (value) => setMobile((prev) => ({ ...prev, otp: value }))
      )}
      {emailEnabled && renderChannel(
        'email', emailState, emailLabel, email,
        sendEmailOtp, verifyEmailOtp,
        (value) => setEmailState((prev) => ({ ...prev, otp: value }))
      )}
    </div>
  );
};

IdentityVerificationPanel.propTypes = {
  postId: PropTypes.number.isRequired,
  mobileNumber: PropTypes.string,
  email: PropTypes.string,
  mobileEnabled: PropTypes.bool,
  emailEnabled: PropTypes.bool,
  mobileLabel: PropTypes.node,
  emailLabel: PropTypes.node,
  onVerified: PropTypes.func.isRequired
};

IdentityVerificationPanel.defaultProps = {
  mobileEnabled: false,
  emailEnabled: false,
  mobileLabel: 'Mobile Number',
  emailLabel: 'Email Address'
};

export default IdentityVerificationPanel;
